/**
 * Type-to-adopt signature input.
 *
 * The signer types their full legal name and sees it rendered in the script
 * face the executed PDF will use, then adopts it. This replaced a drawing
 * canvas: a mouse-drawn scrawl is no more probative than a typed name under
 * E-SIGN/UETA, it is unusable on a trackpad, and it forced a signature IMAGE
 * into the record that had to be stored and fetched to render a document.
 *
 * Shared deliberately — the legal signing flow and the dynamic form renderer's
 * `signature` field both use this, so a signature looks and behaves the same
 * everywhere it is captured.
 */
import { Stack, Text, TextInput, Paper } from "@mantine/core";
import "./typedSignature.css";

export interface TypedSignatureProps {
  /** The typed name. Empty string when nothing has been typed yet. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Field label. Defaults to the legal-signing wording. */
  label?: string;
  /** Marks the input invalid and renders the message beneath it. */
  error?: string;
  /** Forwarded to the input for testing/labelling. */
  "data-testid"?: string;
}

export function TypedSignature({
  value,
  onChange,
  disabled,
  label = "Type your full legal name",
  error,
  "data-testid": testId = "typed-signature-input",
}: TypedSignatureProps) {
  const typed = value.trim();

  return (
    <Stack gap="xs">
      <TextInput
        label={label}
        placeholder="Your full legal name"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        disabled={disabled}
        error={error}
        data-testid={testId}
        autoComplete="name"
      />

      {/*
       * Only shown once something is typed — an empty signature card reads as
       * a rendering fault rather than an empty state.
       */}
      {typed.length > 0 && (
        <Stack gap={4}>
          <Text size="sm" c="dimmed">
            This is how your signature will look:
          </Text>
          <Paper withBorder p="md" radius="sm">
            <div
              className="typedSignaturePreview"
              data-testid="typed-signature-preview"
            >
              {typed}
            </div>
          </Paper>
        </Stack>
      )}
    </Stack>
  );
}
